import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { every, ignoreElements, timestamp } from 'rxjs';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { ApiService } from 'src/app/Services/api.service';
import { CalendarService } from 'src/app/Services/calendar.service';
import { ColorService } from 'src/app/Services/color.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'app-editrateset',
    templateUrl: './editrateset.component.html',
    styleUrls: ['./editrateset.component.scss'],
    standalone: false
})
export class EditratesetComponent extends ModelEditor implements OnInit {

  public override ReactiveFormsEnabled: boolean = false;
  public Grids: any[] = []
  public DayHeaders: string[] = ['Sun', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat'];
  public Months: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  public TimeHeaders: string[] = [];
  public TableHeight: number = 700;
  public SlotMinutes: number = 30;
  public SlotHeight: number = 2;
  public CurrentResizeTimeSpan: any = null;
  public CurrentResizeTrack: any = null;
  public CurrentResizeHandle: string = "";
  public CurrentResizeStartPosition: any = null;
  public CurrentPriceRule: any = null;
  public PriceRules: any[] = [];
  public CustomerGroups: any[] = [];
  public SelectedTabIndex: number = 0;

  @ViewChild('tabsComponent') tabsComponent: any;

  constructor(private colorService: ColorService, public calendarService: CalendarService, private apiService: ApiServiceBase, private injector: Injector) {
    super("parking/ratesets", injector);

  }

  public override DefaultModel(): any {
    return {
      Name: "New Rate Set",
      Periods: [
        { StartDay: 1, StartMonth: 1, EndDay: 31, EndMonth: 12, Timespans: [] }
      ]
    };
  }
  public override AfterModelLoaded(): void {
    // Validate and fix periods to ensure they cover the entire year without overlaps
    this.ValidateAndFixPeriods();
    
    if(this.TimeHeaders.length == 0){ //only do this on initial load, not after save
      this.BuildTimeHeaders();
      this.BuildGrids();
      
      // After grids are built, select the current period
      this.SelectCurrentPeriod();
      
      this.apiService.Get<any>("parking/pricerules").then(result => {
        this.PriceRules = result;
      });
      
      this.apiService.Get<any>("parking/customergroups").then(result => {
        this.CustomerGroups = result;
      });
    }
  }
  
  private ValidateAndFixPeriods(): void {
    if (!this.Model.Periods || this.Model.Periods.length === 0) {
      // If no periods, create default
      this.Model.Periods = [
        { StartDay: 1, StartMonth: 1, EndDay: 31, EndMonth: 12, Timespans: [] }
      ];
      return;
    }
    
    // Sort periods by start date
    this.Model.Periods.sort((a: any, b: any) => {
      if (a.StartMonth !== b.StartMonth) {
        return a.StartMonth - b.StartMonth;
      }
      return a.StartDay - b.StartDay;
    });
    
    // Fix first period to start on Jan 1
    this.Model.Periods[0].StartDay = 1;
    this.Model.Periods[0].StartMonth = 1;
    
    // Fix overlaps and gaps
    for (let i = 1; i < this.Model.Periods.length; i++) {
      let prevPeriod = this.Model.Periods[i - 1];
      let currentPeriod = this.Model.Periods[i];
      
      // Calculate what the start should be (day after previous end)
      let expectedStart = this.calendarService.GetNextDay(prevPeriod.EndDay, prevPeriod.EndMonth);
      currentPeriod.StartDay = expectedStart.getDate();
      currentPeriod.StartMonth = expectedStart.getMonth() + 1;
      
      // Ensure current period doesn't end before it starts
      if (currentPeriod.EndMonth < currentPeriod.StartMonth || 
          (currentPeriod.EndMonth === currentPeriod.StartMonth && currentPeriod.EndDay < currentPeriod.StartDay)) {
        currentPeriod.EndDay = currentPeriod.StartDay;
        currentPeriod.EndMonth = currentPeriod.StartMonth;
      }
    }
    
    // Fix last period to end on Dec 31
    this.Model.Periods[this.Model.Periods.length - 1].EndDay = 31;
    this.Model.Periods[this.Model.Periods.length - 1].EndMonth = 12;
  }
  public override BeforeSave(): void {
    //rebuild each period's timespans from the days displayed in Grids
    let periods = [];
    for (let g of this.Grids) {
      periods.push(g.Period);
      let timespans = [];
      for (let d of g.Days) {
        for(let tr of d.Tracks)
        {
          for (let t of tr.Timespans) {
            t.DayOfWeek = d.Index;
            timespans.push(t);
          }
        }
      }
      g.Period.Timespans = timespans;
    }
    this.Model.Periods = periods;
  }
  public override Validators(): any {

  }

  private BuildGrids() {
    this.SlotHeight = this.TableHeight / (1440 / this.SlotMinutes);
    this.Grids = [];
    for (let p = 0; p < this.Model.Periods.length; p++) {
      let period: any = { Period: this.Model.Periods[p], Days: [] };
      this.Grids.push(period);
      this.BuildEmptyDays(period);
    }
  }
  private BuildEmptyDays(period: any) {
    for (let d = 0; d < 7; d++) {
      let day: any = { Index: d, Header: this.DayHeaders[d], Tracks: [{ Slots: [], Timespans: [] }] };
      period.Days.push(day);
      if (period.Period.Timespans == null || !Array.isArray(period.Period.Timespans)) {
        period.Period.Timespans = [];
      }
      for (let i = 0; i < period.Period.Timespans.length; i++) {
        if (period.Period.Timespans[i].DayOfWeek == d) {
          let span = period.Period.Timespans[i];
          this.UpdateTimeSpanPosition(span);
          if(span.TrackIndex >= day.Tracks.length) 
            day.Tracks.push({ Slots: [], Timespans: [], Index: day.Tracks.length });
          day.Tracks[span.TrackIndex].Timespans.push(span);
        }
      }
      for (let minute = 0; minute < 1440; minute += this.SlotMinutes) {
        for(let t of day.Tracks){
          let slot: any = { StartsAt: minute, EndsAt: minute + this.SlotMinutes };
          t.Slots.push(slot);
        }
      }
    }
  }
  private BuildTimeHeaders() {
    if(this.TimeHeaders.length > 0) return;
    this.TimeHeaders = [];
    for (let i = 0; i < 1440; i += this.SlotMinutes) {
      this.TimeHeaders.push(Math.floor(i / 60) + ":" + ((i % 60) < 10 ? '0' : '') + (i % 60));
    }
    this.TimeHeaders.push("0:00")
  }

  private UpdateTimeSpanPosition(timespan: any) {
    if (timespan.EndHour >= 24) {
      timespan.EndHour = 24;
      timespan.EndMinute = 0;
    }
    if (timespan.StartHour < 0) {
      timespan.StartHour = 0;
      timespan.StartMinute = 0;
    }

    let top = ((timespan.StartHour * 60 + timespan.StartMinute) / 1440) * this.TableHeight;
    let bottom = ((timespan.EndHour * 60 + timespan.EndMinute) / 1440) * this.TableHeight;
    let height = bottom - top;
    timespan.Top = top;
    timespan.PreDragTop = top;
    timespan.Height = height;
    timespan.PreDragHeight = height;
    timespan.StartMinuteIndex = timespan.StartHour * 60 + timespan.StartMinute;
    timespan.EndMinuteIndex = timespan.EndHour * 60 + timespan.EndMinute;
    timespan.TextClass = this.colorService.GetVisibleTextClassFor(timespan.PriceRuleColor);
  }

  public TabChanged(index: Number) {
    this.SelectedTabIndex = index as number;
  }

  private SelectCurrentPeriod(): void {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth() + 1; // JavaScript months are 0-based
    
    // Find the period that contains the current date
    for (let i = 0; i < this.Grids.length; i++) {
      const period = this.Grids[i].Period;
      
      if (this.isDateInPeriod(currentDay, currentMonth, period)) {
        this.SelectedTabIndex = i;
        
        // Directly select the tab if possible, otherwise use a timeout for view initialization
        if (this.tabsComponent && this.tabsComponent.tabs && this.tabsComponent.tabs.length > i) {
          this.tabsComponent.selectTab(this.tabsComponent.tabs.toArray()[i]);
        } else {
          // View not ready yet, try after a short delay
          setTimeout(() => {
            if (this.tabsComponent && this.tabsComponent.tabs && this.tabsComponent.tabs.length > i) {
              this.tabsComponent.selectTab(this.tabsComponent.tabs.toArray()[i]);
            }
          }, 100);
        }
        return;
      }
    }
    
    // If no period found (shouldn't happen with valid data), default to first period
    this.SelectedTabIndex = 0;
  }

  private isDateInPeriod(day: number, month: number, period: any): boolean {
    const startDate = new Date(2024, period.StartMonth - 1, period.StartDay); // Use a leap year for consistency
    const endDate = new Date(2024, period.EndMonth - 1, period.EndDay);
    const checkDate = new Date(2024, month - 1, day);
    
    // Handle year-spanning periods (like Dec 15 to Feb 28)
    if (startDate > endDate) {
      // Period spans across year boundary
      return checkDate >= startDate || checkDate <= endDate;
    } else {
      // Normal period within the same year
      return checkDate >= startDate && checkDate <= endDate;
    }
  }

  public trackByPeriod(index: number, item: any): any {
    // Use a combination of index and period properties to create a stable identifier
    return `${index}_${item.Period.StartDay}_${item.Period.StartMonth}_${item.Period.EndDay}_${item.Period.EndMonth}`;
  }

  public StartDrag(evt: any, handle: string, timespan: any, track: any) {
    this.CurrentResizeTimeSpan = timespan;
    this.CurrentResizeTrack = track;;
    this.CurrentResizeHandle = handle;
    this.CurrentResizeStartPosition = evt.pageY;
  }
  public EndDrag(evt: any) {
    if (this.CurrentResizeTimeSpan == null) {
      return;
    }
    let timespan = this.CurrentResizeTimeSpan;
    if (this.CurrentResizeHandle == 'bottom') {
      let newEndMins = timespan.StartHour * 60 + timespan.StartMinute + (Math.round(timespan.Height / this.SlotHeight) * this.SlotMinutes);
      this.CurrentResizeTimeSpan.EndHour = Math.floor(newEndMins / 60);
      this.CurrentResizeTimeSpan.EndMinute = newEndMins % 60;
      this.UpdateTimeSpanPosition(this.CurrentResizeTimeSpan);
      this.CheckCollisions(this.CurrentResizeTrack, this.CurrentResizeTimeSpan);
    }
    else if (this.CurrentResizeHandle == 'top') {
      let newStartMins = Math.round(timespan.Top / this.SlotHeight) * this.SlotMinutes;
      this.CurrentResizeTimeSpan.StartHour = Math.floor(newStartMins / 60);
      this.CurrentResizeTimeSpan.StartMinute = newStartMins % 60;
      this.UpdateTimeSpanPosition(this.CurrentResizeTimeSpan);
      this.CheckCollisions(this.CurrentResizeTrack, this.CurrentResizeTimeSpan);
    }
    this.CurrentResizeTimeSpan = null;
  }
  public CancelDrag(evt: any) {
    if (this.CurrentResizeTimeSpan != null) {
      this.UpdateTimeSpanPosition(this.CurrentResizeTimeSpan);
      this.CurrentResizeTimeSpan = null;
    }
  }

  public ColumnMouseMove(evt: any) {
    if (this.CurrentResizeTimeSpan != null) {
      if (evt.buttons != 1) {//if not still dragging then cancel resize
        this.CurrentResizeTimeSpan = null;
        return;
      }
      let totalYdrag = evt.pageY - this.CurrentResizeStartPosition;
      if (totalYdrag != 0) {
        if (this.CurrentResizeHandle == "bottom") {
          this.CurrentResizeTimeSpan.Height = this.CurrentResizeTimeSpan.PreDragHeight + totalYdrag;

        }
        else if (this.CurrentResizeHandle == "top") {
          this.CurrentResizeTimeSpan.Top = this.CurrentResizeTimeSpan.PreDragTop + totalYdrag;
          this.CurrentResizeTimeSpan.Height = this.CurrentResizeTimeSpan.PreDragHeight - totalYdrag;
        }
        if (this.CurrentResizeTimeSpan.Height < this.SlotHeight) {
          this.CurrentResizeTimeSpan.Height = this.SlotHeight;
          if (this.CurrentResizeHandle == "top") {
            this.CurrentResizeTimeSpan.Top = this.CurrentResizeTimeSpan.PreDragTop + this.CurrentResizeTimeSpan.PreDragHeight - this.SlotHeight;
          }
        }
      }
    }
  }

  public CheckCollisions(track: any, timespanMoved: any) {
    for (let i = 0; i < track.Timespans.length; i++) {
      if (track.Timespans[i] == timespanMoved) {
        continue;
      }
      let t = track.Timespans[i];
      if (timespanMoved.StartMinuteIndex < t.StartMinuteIndex && timespanMoved.EndMinuteIndex > t.StartMinuteIndex) {
        //moved timespan ran over the start of the old timespan
        if (timespanMoved.EndMinuteIndex < t.EndMinuteIndex) {
          //it took time off the start but there is still some of t remaining
          t.StartHour = timespanMoved.EndHour;
          t.StartMinute = timespanMoved.EndMinute;
          this.UpdateTimeSpanPosition(t);
        }
        else {
          //the move timespan eliminated t
          track.Timespans.splice(i, 1);
          i -= 1;
        }
      }
      else if (timespanMoved.StartMinuteIndex > t.StartMinuteIndex && timespanMoved.StartMinuteIndex < t.EndMinuteIndex) {
        //moved timespan up and ran over the end of another timespan
        t.EndHour = timespanMoved.StartHour;
        t.EndMinute = timespanMoved.StartMinute;
        this.UpdateTimeSpanPosition(t);
      }
      else if (timespanMoved.StartMinuteIndex < t.StartMinuteIndex && timespanMoved.EndMinuteIndex > t.EndMinuteIndex) {
        //moved timespan has eliminated t
        track.Timespans.splice(i, 1);
        i -= 1;
      }
      else if (timespanMoved.StartMinuteIndex == t.EndMinuteIndex && timespanMoved.PriceRuleId == t.PriceRuleId && t.DenyEntry == timespanMoved.DenyEntry && timespanMoved.DisableMergePrior == t.DisableMergePrior && timespanMoved.IsFinalSpanForIteration == t.IsFinalSpanForIteration) {
        //butted up against another timespan that is the same rule. remove t and move the start up to where it was
        timespanMoved.StartHour = t.StartHour;
        timespanMoved.StartMinute = t.StartMinute;
        this.UpdateTimeSpanPosition(timespanMoved);
        track.Timespans.splice(i, 1);
        i -= 1;
      }
      else if (timespanMoved.EndMinuteIndex == t.StartMinuteIndex && timespanMoved.PriceRuleId == t.PriceRuleId && t.DenyEntry == timespanMoved.DenyEntry && timespanMoved.DisableMergePrior == t.DisableMergePrior && timespanMoved.IsFinalSpanForIteration == t.IsFinalSpanForIteration) {
        //butted up against another timespan that is the same rule. remove t and move the start up to where it was
        timespanMoved.EndHour = t.EndHour;
        timespanMoved.EndMinute = t.EndMinute;
        this.UpdateTimeSpanPosition(timespanMoved);
        track.Timespans.splice(i, 1);
        i -= 1;
      }
    }
  }

  public SelectPriceRule(rule: any) {
    if (this.CurrentPriceRule != null) {
      this.CurrentPriceRule.Selected = false;
      if (this.CurrentPriceRule == rule) {
        this.CurrentPriceRule = null; //deselect
        return;
      }
    }
    this.CurrentPriceRule = rule;
    rule.Selected = true;
  }

  public EmptySlotClicked(slot: any, track: any) {
    if (this.CurrentPriceRule == null) {
      return;
    }
    let newTimespan = {
      StartHour: Math.floor(slot.StartsAt / 60),
      StartMinute: slot.StartsAt % 60,
      EndHour: Math.floor(slot.EndsAt / 60),
      EndMinute: slot.EndsAt % 60,
      PriceRuleColor: this.CurrentPriceRule.Color,
      PriceRuleId: this.CurrentPriceRule.Id,
      PriceRuleName: this.CurrentPriceRule.Name,
      DenyEntry: false,
      DisableMergePrior: false,
      IsFinalSpanForIteration: false,
      TrackIndex: track.Index,
    };
    this.UpdateTimeSpanPosition(newTimespan);
    track.Timespans.push(newTimespan);
  }

  public PeriodMonthChanged(period: any, index: number) {
    // Adjust days to be within the valid range for the selected month
    let maxDays = this.calendarService.MaxDaysInMonth(period.StartMonth);
    if (period.StartDay > maxDays) {
      period.StartDay = maxDays;
    }
    maxDays = this.calendarService.MaxDaysInMonth(period.EndMonth);
    if (period.EndDay > maxDays) {
      period.EndDay = maxDays;
    }
    // Use the same comprehensive logic as PeriodChanged
    this.PeriodChanged(period, index);
  }

  public PeriodChanged(period: any, index: number) {
    // Store reference to the period being edited
    let editedPeriod = period;
    
    // Critical rule: First period MUST start on Jan 1
    if (index === 0) {
      period.StartMonth = 1;
      period.StartDay = 1;
    }
    
    // Critical rule: Last period MUST end on Dec 31
    if (index === this.Grids.length - 1) {
      period.EndMonth = 12;
      period.EndDay = 31;
    }
    
    // Ensure end date is not before start date for the current period
    if (period.EndMonth < period.StartMonth || 
        (period.EndMonth === period.StartMonth && period.EndDay < period.StartDay)) {
      period.EndMonth = period.StartMonth;
      period.EndDay = period.StartDay;
    }

    // Step 1: Adjust previous period's end date based on user's selection
    if (index > 0) {
      for (let i = 0; i < index; i++) {
        let priorPeriod = this.Grids[i].Period;
        let priorStartValue = priorPeriod.StartMonth * 31 + priorPeriod.StartDay;
        let priorEndValue = priorPeriod.EndMonth * 31 + priorPeriod.EndDay;
        let editedStartValue = editedPeriod.StartMonth * 31 + editedPeriod.StartDay;

        // If the edited period starts before the prior period, remove the prior period
        if (editedStartValue <= priorStartValue) {
          this.Grids.splice(i, 1);
          this.Model.Periods.splice(i, 1);
          i--; // Adjust index after removal
          index--; // Adjust the index of the edited period
        } else if (priorEndValue > editedStartValue) {
          // If the prior period ends before the edited period starts, adjust the end date
          let newEnd = this.calendarService.GetPrevDay(editedPeriod.StartDay, editedPeriod.StartMonth);
          priorPeriod.EndMonth = newEnd.getMonth() + 1;
          priorPeriod.EndDay = newEnd.getDate();
        }
      }
    }
    
    // Step 2: Adjust next period's start date based on user's selection
    if (index < this.Grids.length - 1) {
      for (let i = index + 1; i < this.Grids.length; i++) {
        let nextPeriod = this.Grids[i].Period;
        let nextStartValue = nextPeriod.StartMonth * 31 + nextPeriod.StartDay;
        let nextEndValue = nextPeriod.EndMonth * 31 + nextPeriod.EndDay;
        let editedEndValue = editedPeriod.EndMonth * 31 + editedPeriod.EndDay;

        // If the edited period ends after the next period starts, remove the next period
        if (editedEndValue >= nextEndValue) {
          this.Grids.splice(i, 1);
          this.Model.Periods.splice(i, 1);
          i--; // Adjust index after removal
        } else if (editedEndValue >= nextStartValue ) {
          // If the next period starts after the edited period ends, adjust the start date
          let newStart = this.calendarService.GetNextDay(editedPeriod.EndDay, editedPeriod.EndMonth);
          nextPeriod.StartMonth = newStart.getMonth() + 1;
          nextPeriod.StartDay = newStart.getDate();
          break; // No need to check further as periods are sequential
        }
      }
    }

    // // Step 3: Remove any periods that have become invalid
    // for (let i = this.Grids.length - 1; i >= 0; i--) {
    //   let p = this.Grids[i].Period;
    //   if (p === editedPeriod) continue; // Don't remove the period being edited
      
    //   let startValue = p.StartMonth * 31 + p.StartDay;
    //   let endValue = p.EndMonth * 31 + p.EndDay;
      
    //   // A period is invalid if its end is before its start
    //   if (endValue < startValue) {
    //     this.Grids.splice(i, 1);
    //   }
    //}
    
    // Step 4: Ensure critical rules after removals
    if (this.Grids.length > 0) {
      // First period must start Jan 1
      this.Grids[0].Period.StartDay = 1;
      this.Grids[0].Period.StartMonth = 1;
      
      // Last period must end Dec 31
      this.Grids[this.Grids.length - 1].Period.EndDay = 31;
      this.Grids[this.Grids.length - 1].Period.EndMonth = 12;
      
      // Now adjust periods to maintain continuity while respecting user's edits
      for (let i = 0; i < this.Grids.length; i++) {
        let currPeriod = this.Grids[i].Period;
        
        // For the edited period, we need to adjust adjacent periods to accommodate it
        if (currPeriod === editedPeriod) {
          // Adjust previous period to end the day before (if not first)
          if (i > 0) { // edited period is not first
            let prevPeriod = this.Grids[i - 1].Period;
            let newPrevEnd = this.calendarService.GetPrevDay(currPeriod.StartDay, currPeriod.StartMonth);
            prevPeriod.EndMonth = newPrevEnd.getMonth() + 1;
            prevPeriod.EndDay = newPrevEnd.getDate();
          }
          
          // Adjust next period to start the day after (if not last)
          if (i < this.Grids.length - 1) { // edited period is not last
            let nextPeriod = this.Grids[i + 1].Period;
            let newNextStart = this.calendarService.GetNextDay(currPeriod.EndDay, currPeriod.EndMonth);
            nextPeriod.StartMonth = newNextStart.getMonth() + 1;
            nextPeriod.StartDay = newNextStart.getDate();
          }
        }
      }
      
      // Fill any remaining gaps for non-edited periods
      for (let i = 1; i < this.Grids.length; i++) {
        let prevPeriod = this.Grids[i - 1].Period;
        let currPeriod = this.Grids[i].Period;
        
        // Skip if this is the edited period (unless it's now the first period)
        if (currPeriod === editedPeriod && i !== 0) {
          continue;
        }
        
        // Calculate expected start
        let expectedStart = this.calendarService.GetNextDay(prevPeriod.EndDay, prevPeriod.EndMonth);
        let expectedStartValue = (expectedStart.getMonth() + 1) * 31 + expectedStart.getDate();
        let actualStartValue = currPeriod.StartMonth * 31 + currPeriod.StartDay;
        
        // Only adjust if there's a gap
        if (expectedStartValue !== actualStartValue) {
          currPeriod.StartDay = expectedStart.getDate();
          currPeriod.StartMonth = expectedStart.getMonth() + 1;
        }
      }
    } else {
      // This should never happen, but as a safety net
      this.CreateDefaultPeriod();
    }
  }
  
  private EnsurePeriodContinuity() {
    // Make sure each period starts the day after the previous one ends
    for (let i = 1; i < this.Grids.length; i++) {
      let prevPeriod = this.Grids[i - 1].Period;
      let currentPeriod = this.Grids[i].Period;
      
      let expectedStart = this.calendarService.GetNextDay(prevPeriod.EndDay, prevPeriod.EndMonth);
      currentPeriod.StartDay = expectedStart.getDate();
      currentPeriod.StartMonth = expectedStart.getMonth() + 1;
    }
  }
  
  private CreateDefaultPeriod() {
    let newPeriod = { 
      Period: { StartDay: 1, StartMonth: 1, EndDay: 31, EndMonth: 12, Timespans: [] }, 
      Days: [] 
    };
    this.BuildEmptyDays(newPeriod);
    this.Grids = [newPeriod];
  }

  public SplitPeriod(index: number) {
    let period = this.Grids[index].Period;
    
    // Can't split if the period is only one day
    if (period.StartDay === period.EndDay && period.StartMonth === period.EndMonth) {
      return;
    }

    // Calculate the new end date (one day before current end)
    let newEnd = this.calendarService.GetPrevDay(period.EndDay, period.EndMonth);
    
    // Create new period starting from the original end date
    let newPeriod = { 
      Period: { 
        StartDay: period.EndDay, 
        StartMonth: period.EndMonth, 
        EndDay: period.EndDay, 
        EndMonth: period.EndMonth, 
        Timespans: [] 
      }, 
      Days: [] 
    };
    
    // If this is the last period, the new period should end on Dec 31
    if (index === this.Grids.length - 1) {
      newPeriod.Period.EndDay = 31;
      newPeriod.Period.EndMonth = 12;
    } else {
      // Otherwise, it should end the day before the next period starts
      let nextPeriod = this.Grids[index + 1].Period;
      let dayBeforeNext = this.calendarService.GetPrevDay(nextPeriod.StartDay, nextPeriod.StartMonth);
      newPeriod.Period.EndDay = dayBeforeNext.getDate();
      newPeriod.Period.EndMonth = dayBeforeNext.getMonth() + 1;
    }
    
    // Update the current period's end date
    period.EndDay = newEnd.getDate();
    period.EndMonth = newEnd.getMonth() + 1;
    
    // Build days for the new period
    this.BuildEmptyDays(newPeriod);
    
    // Insert the new period after the current one
    this.Grids.splice(index + 1, 0, newPeriod);
  }

  public AddNewPeriod() {
    // Always split the last period
    if (this.Grids.length > 0) {
      this.SplitPeriod(this.Grids.length - 1);
    }
  }

  public DeletePeriod(index: number) {
    // Must have at least one period
    if (this.Grids.length <= 1) {
      return;
    }

    // Remove the period
    this.Grids.splice(index, 1);
    this.Model.Periods.splice(index, 1);
    
    // Ensure continuity after deletion
    this.EnsurePeriodContinuity();
    
    // Ensure first period starts Jan 1
    this.Grids[0].Period.StartDay = 1;
    this.Grids[0].Period.StartMonth = 1;
    
    // Ensure last period ends Dec 31
    this.Grids[this.Grids.length - 1].Period.EndDay = 31;
    this.Grids[this.Grids.length - 1].Period.EndMonth = 12;
  }

  public CopyFromPeriod(targetIndex: number, event: any) {
    const sourceIndex = parseInt(event.target.value);
    
    // Reset the dropdown to default state
    event.target.value = '';
    
    // Validate indices
    if (isNaN(sourceIndex) || sourceIndex < 0 || sourceIndex >= this.Grids.length || sourceIndex === targetIndex) {
      return;
    }

    const sourcePeriod = this.Grids[sourceIndex];
    const targetPeriod = this.Grids[targetIndex];

    // Clear existing timespans in target period
    targetPeriod.Period.Timespans = [];
    
    // Reset all days in the target period to have only one track each
    for (let day of targetPeriod.Days) {
      // Clear all tracks and create a single empty track
      day.Tracks = [{ Slots: [], Timespans: [], Index: 0 }];
      
      // Rebuild slots for the single track
      for (let minute = 0; minute < 1440; minute += this.SlotMinutes) {
        let slot: any = { StartsAt: minute, EndsAt: minute + this.SlotMinutes };
        day.Tracks[0].Slots.push(slot);
      }
    }

    // Collect timespans from the source period's current editor state (not from saved data)
    let sourceTimespans = [];
    for (let d of sourcePeriod.Days) {
      for (let tr of d.Tracks) {
        for (let t of tr.Timespans) {
          // Create a copy of the timespan with the day and track info
          sourceTimespans.push({
            ...t,
            DayOfWeek: d.Index,
            TrackIndex: tr.Index || 0
          });
        }
      }
    }

    // Copy timespans from source to target with new GUIDs
    if (sourceTimespans.length > 0) {
      targetPeriod.Period.Timespans = sourceTimespans.map((timespan: any) => {
        return {
          ...timespan, // Copy all properties including current editor state
          Id: uuidv4(), // Generate new GUID for the copied timespan
          DayOfWeek: timespan.DayOfWeek,
          TrackIndex: timespan.TrackIndex
        };
      });

      // Rebuild the visual display for the target period
      this.RebuildPeriodDisplay(targetIndex);
    }
  }

  private RebuildPeriodDisplay(periodIndex: number) {
    const period = this.Grids[periodIndex];
    
    // Clear all tracks' timespans
    for (let day of period.Days) {
      for (let track of day.Tracks) {
        track.Timespans = [];
      }
    }

    // Redistribute timespans into the correct days and tracks
    if (period.Period.Timespans) {
      for (let timespan of period.Period.Timespans) {
        const dayIndex = timespan.DayOfWeek;
        if (dayIndex >= 0 && dayIndex < period.Days.length) {
          const day = period.Days[dayIndex];
          
          // Ensure we have enough tracks
          while (day.Tracks.length <= timespan.TrackIndex) {
            this.AddTrack(day);
          }
          
          // Update timespan position and add to the correct track
          this.UpdateTimeSpanPosition(timespan);
          day.Tracks[timespan.TrackIndex].Timespans.push(timespan);
        }
      }
    }
  }

  public DeleteTimespan(track: any, index: number) {
    track.Timespans.splice(index, 1);
  }

  public AddTrack(day: any){
    let newTrack = { Slots: <any>[], Timespans: <any>[], Index: day.Tracks.length };
    day.Tracks.push(newTrack);
    for (let minute = 0; minute < 1440; minute += this.SlotMinutes) {
        let slot: any = { StartsAt: minute, EndsAt: minute + this.SlotMinutes };
        newTrack.Slots.push(slot);
    }
  }

}
